// ─── teamChatSound ───────────────────────────────────────────────────────────
//
// A tiny, asset-free notification chime for the studio Team Chat plus the
// mute-preference helpers that gate it.
//
// The chime is synthesized with the Web Audio API (two short notes, ~880Hz
// then ~1320Hz) so there's no audio file to bundle, load, or 404. Everything
// is wrapped in try/catch: browsers block audio until the first user gesture,
// AudioContext can be unavailable (SSR, locked-down embeds), and a chime
// failing must never bubble out of the realtime handler that calls it.
//
// Mute state lives in localStorage under 'planos:teamChatMuted' so the
// preference survives reloads and is shared across the app (the chat header
// toggle and the app-wide notifier both read it).

const MUTE_KEY = 'planos:teamChatMuted'

// Reuse one AudioContext across chimes — creating a new one per message both
// wastes resources and trips per-context autoplay heuristics.
let audioCtx = null

function getAudioContext() {
  if (audioCtx) return audioCtx
  const Ctor = typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext)
  if (!Ctor) return null
  audioCtx = new Ctor()
  return audioCtx
}

// Schedule a single note: a sine tone with a quick gain fade-in/out so the
// note doesn't click at its edges.
function playNote(ctx, freq, startAt, duration) {
  const osc = ctx.createOscillator()
  const gain = ctx.createGain()
  osc.type = 'sine'
  osc.frequency.setValueAtTime(freq, startAt)

  const peak = 0.05
  gain.gain.setValueAtTime(0.0001, startAt)
  gain.gain.exponentialRampToValueAtTime(peak, startAt + 0.012)
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration)

  osc.connect(gain)
  gain.connect(ctx.destination)
  osc.start(startAt)
  osc.stop(startAt + duration + 0.02)
}

/**
 * Play a short pleasant two-note chime. Fails silently if audio is blocked
 * (e.g. before the first user gesture) or unsupported.
 */
export function playTeamChatSound() {
  try {
    const ctx = getAudioContext()
    if (!ctx) return
    // resume() returns a promise; ignore it (and any rejection) — if the
    // context is still suspended the notes simply won't sound, which is fine.
    if (ctx.state === 'suspended' && typeof ctx.resume === 'function') {
      ctx.resume().catch(() => {})
    }
    const now = ctx.currentTime
    const noteLen = 0.12
    playNote(ctx, 880, now, noteLen)
    playNote(ctx, 1320, now + noteLen, noteLen)
  } catch {
    // Autoplay blocked or AudioContext unavailable — stay silent.
  }
}

/**
 * True when the planner has muted the Team Chat sound. Default: unmuted.
 */
export function isTeamChatMuted() {
  try {
    return localStorage.getItem(MUTE_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * Persist the mute preference. Passing a falsy value clears the key so the
 * default (unmuted) is restored.
 */
export function setTeamChatMuted(muted) {
  try {
    if (muted) localStorage.setItem(MUTE_KEY, '1')
    else localStorage.removeItem(MUTE_KEY)
  } catch {
    // localStorage unavailable (private mode quota, disabled) — no-op.
  }
}
